import { useEffect, useRef } from 'react'

/**
 * Asks for the next page when the end of a list comes into view.
 *
 * **So a board is one list and not eighty pages.** With fifty rows a page, an
 * organisation with four thousand contacts had Previous and Next and no way to
 * look at its own list — and the search and the filters, which are how somebody
 * actually finds a person, could only narrow the page they happened to be on.
 *
 * Rows are still fetched a page at a time. Everything at once would be a table
 * of forty thousand rows, which is a browser that stops answering; this keeps
 * the request small and lets the scrollbar be the thing that asks.
 *
 * **It renders a real element**, not a hook on the window, because what matters
 * is whether the end of *this* list is visible — a page with its own scrolling
 * region, or a list inside a dialog, has nothing to do with the window.
 */
export function MoreWhenSeen({
  onMore,
  disabled = false,
}: {
  readonly onMore: () => void
  /** True when there is nothing left, so the observer is not armed at all. */
  readonly disabled?: boolean
}) {
  const sentinel = useRef<HTMLDivElement>(null)
  /*
   * The callback is held in a ref so the observer is built once per mount
   * rather than on every render. `onMore` is rebuilt whenever the rows change —
   * which is every time it fires — and an observer rebuilt in that effect
   * disconnects and reconnects mid-scroll, firing again immediately.
   */
  const latest = useRef(onMore)
  latest.current = onMore

  useEffect(() => {
    const element = sentinel.current
    if (element === null || disabled) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) latest.current()
      },
      // Ask a little before the end arrives, so scrolling does not stop dead
      // while the next page is in flight.
      { rootMargin: '400px' },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [disabled])

  return <div aria-hidden="true" ref={sentinel} />
}
